import { supabase } from '../supabase';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'consultant' | 'designer';
  active: boolean;
}

export async function login(email: string, password: string): Promise<User | null> {
  const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (authError || !authData.user) {
    console.error('Login error:', authError);
    return null;
  }

  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', authData.user.id)
    .eq('active', true)
    .maybeSingle();

  if (profileError || !profile) {
    console.error('Profile error:', profileError);
    await supabase.auth.signOut();
    return null;
  }

  return {
    id: profile.id,
    email: profile.email,
    name: profile.name,
    role: profile.role,
    active: profile.active,
  };
}

export async function logout(): Promise<void> {
  await supabase.auth.signOut();
}

export async function getCurrentUser(): Promise<User | null> {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError) {
      console.error('Session error:', sessionError);
      return null;
    }

    if (!session?.user) {
      return null;
    }

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .eq('active', true)
      .maybeSingle();

    if (error) {
      console.error('Profile fetch error:', error);
      return null;
    }

    if (!profile) {
      console.warn('No profile found for authenticated user:', session.user.id);
      return null;
    }

    return {
      id: profile.id,
      email: profile.email,
      name: profile.name,
      role: profile.role,
      active: profile.active,
    };
  } catch (error) {
    console.error('Unexpected error in getCurrentUser:', error);
    return null;
  }
}

export async function getAllUsers(): Promise<User[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, email, name, role, active')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching users:', error);
    return [];
  }

  return data || [];
}

export async function createUser(userData: {
  email: string;
  password: string;
  name: string;
  role: 'owner' | 'consultant' | 'designer';
}): Promise<User | null> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      console.error('No active session');
      return null;
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/create-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('Error creating user:', error);
      throw new Error(error.error || 'Failed to create user');
    }

    const profile = await response.json();
    return profile;
  } catch (error) {
    console.error('Error creating user:', error);
    return null;
  }
}

export async function updateUser(
  userId: string,
  updates: Partial<{
    name: string;
    role: 'owner' | 'consultant' | 'designer';
    active: boolean;
  }>
): Promise<User | null> {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select('id, email, name, role, active')
    .single();

  if (error) {
    console.error('Error updating user:', error);
    return null;
  }

  return data;
}

export async function deleteUser(userId: string): Promise<boolean> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      console.error('No active session');
      return false;
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/delete-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('Error deleting user:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error deleting user:', error);
    return false;
  }
}

export function hasPermission(
  user: User | null,
  permission: 'manage_users' | 'manage_clients' | 'write_copy' | 'upload_visuals'
): boolean {
  if (!user || !user.active) return false;

  switch (permission) {
    case 'manage_users':
      return user.role === 'owner';

    case 'manage_clients':
      return user.role === 'owner' || user.role === 'consultant';

    case 'write_copy':
      return user.role === 'owner' || user.role === 'consultant';

    case 'upload_visuals':
      return true;

    default:
      return false;
  }
}
